/**
 * The words and choices of the Repeats picker: the short menu a day gives, the line that says what a pattern
 * means, and the next few dates. The dates and the rhythm come from `shared/live-series.ts`, the same rule the
 * server plans sessions from, so the picker never says a day the server will not use.
 */
import {
  clockWords,
  dayName,
  endsTail,
  firstOn,
  nthWord,
  patternDates,
  rhythmLabel,
  samePattern,
  SERIES_MAX_COUNT,
  type SeriesMonthBy,
  type SeriesPattern,
  wallDayWords,
  weekdayOf,
} from "../../shared/live-series";

const NEVER: SeriesPattern["ends"] = { kind: "never" };
const WEEKDAYS = [1, 2, 3, 4, 5];

/** The days of the week in the order the host's browser starts them: Sunday first when it cannot say. Display only. */
export function weekOrder(): number[] {
  // Why both: Chrome and Safari have the method, older Chrome has the property, Firefox neither.
  const locale = new Intl.Locale(navigator.language) as { getWeekInfo?: () => { firstDay: number }; weekInfo?: { firstDay: number } };
  const info = locale.getWeekInfo?.() ?? locale.weekInfo;
  const start = (info?.firstDay ?? 7) % 7;
  return Array.from({ length: 7 }, (_, i) => (start + i) % 7);
}

/** The rhythm alone for a pattern whose first session is on `date`: "Every Tuesday and Thursday". */
export function rhythmWords(p: SeriesPattern, date: string): string {
  return rhythmLabel({ ...p, firstDate: firstOn(p, date) });
}

/** "Every Tuesday and Thursday at 6:00 PM ET, until December 15": the whole line under the control. */
export function patternSentence(p: SeriesPattern, date: string, time: string, timezone: string): string {
  const first = firstOn(p, date);
  return `${rhythmWords(p, date)} at ${clockWords(first, time, timezone)}${endsTail(p.ends, first)}`;
}

// Why: a no-break space (code 160) holds each date, and the dots to the last date, in one piece.
const TIE = String.fromCharCode(160);

/** "Next: Tue Oct 27, Thu Oct 29 ...", with how many there are in all when it ends. Each date and the dots are tied, so no line ends on half a date or a lone "...". */
export function nextLine(p: SeriesPattern, date: string, fromSeq = 1, show = 4): string {
  const dates = patternDates(p, date, show + 1, fromSeq);
  const total = p.ends.kind === "never" ? null : patternDates(p, date, SERIES_MAX_COUNT, fromSeq).length;
  const tail = dates.length > show ? `${TIE}...` : total !== null && total > 1 ? ` (${total} ${fromSeq > 1 ? "to come" : "in all"})` : "";
  return `Next: ${dates.slice(0, show).map((d) => wallDayWords(d).replaceAll(" ", TIE)).join(", ")}${tail}`;
}

/** One line of the short menu; `pattern` is null for Just once. */
export interface QuickChoice {
  id: string;
  label: string;
  pattern: SeriesPattern | null;
}

function make(unit: SeriesPattern["unit"], every: number, weekdays: number[]): SeriesPattern {
  return { unit, every, weekdays, monthBy: "nth", ends: NEVER };
}

/** The menu a day gives: Just once, then each way to repeat from that day. */
export function quickChoices(date: string): QuickChoice[] {
  const wd = weekdayOf(date);
  return [
    { id: "once", label: "Just once", pattern: null },
    { id: "day", label: "Every day", pattern: make("day", 1, []) },
    { id: "weekday", label: "Every weekday (Monday to Friday)", pattern: make("week", 1, WEEKDAYS) },
    { id: "week", label: `Every week on ${dayName(wd)}`, pattern: make("week", 1, [wd]) },
    { id: "2week", label: `Every other week on ${dayName(wd)}`, pattern: make("week", 2, [wd]) },
    { id: "month", label: `Every month on the ${nthWord(date)} ${dayName(wd)}`, pattern: make("month", 1, []) },
  ];
}

/** The choice a pattern is, or null when it is a custom one. */
export function quickOf(p: SeriesPattern | null, date: string): QuickChoice | null {
  const choices = quickChoices(date);
  if (!p) return choices[0];
  return choices.find((c) => c.pattern && samePattern(c.pattern, p)) ?? null;
}

/** The custom dialog's start for a day: every week, on that weekday. */
export function startingPattern(date: string): SeriesPattern {
  return make("week", 1, [weekdayOf(date)]);
}

/** The two ways a monthly pattern can keep its day, named from the first day. */
export function monthChoices(date: string): Array<{ id: SeriesMonthBy; label: string }> {
  const [, , d] = date.split("-").map(Number);
  return [
    { id: "day", label: `On day ${d}` },
    { id: "nth", label: `On the ${nthWord(date)} ${dayName(weekdayOf(date))}` },
  ];
}

/** The pattern a series' rule already is, for Change the series to open on. */
export function patternOfRule(rule: SeriesPattern): SeriesPattern {
  return { unit: rule.unit, every: rule.every, weekdays: rule.weekdays, monthBy: rule.monthBy, ends: rule.ends };
}

/** The pattern after the host moves the first day: a menu choice follows the new day, a custom one stays as it is. */
export function followDay(p: SeriesPattern, from: string, to: string): SeriesPattern {
  const was = from ? quickOf(p, from) : null;
  const now = was ? quickChoices(to).find((c) => c.id === was.id) : null;
  return now?.pattern ? { ...now.pattern, ends: p.ends } : p;
}
